import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import { match } from "ts-pattern"
import { t } from "@lingui/macro"
import { CancellationPeriod, CruisePaymentTermsResponse } from "../../_common/dot-com-api-types"
import { NON_BREAKING_SPACE } from "../../_common/string"

type RefundabilityInfo = {
    isRefundable: boolean
    content: string
}

const defaultRefundabilityInfo: RefundabilityInfo = { isRefundable: false, content: NON_BREAKING_SPACE }

export const currentCancellationPeriod = O.fold<
    CruisePaymentTermsResponse,
    CruisePaymentTermsResponse["currentCancellationPeriod"]
>(
    () => O.none,
    (paymentResponse) =>
        F.pipe(
            paymentResponse.currentCancellationPeriod,
            O.alt(() => F.pipe(paymentResponse.cancellationPeriods, O.chain(ROA.head))),
        ),
)

export const depositLabel = O.fold<CruisePaymentTermsResponse, RefundabilityInfo>(
    () => defaultRefundabilityInfo,
    (paymentResponse: CruisePaymentTermsResponse) => {
        const cancellationPeriod = O.toUndefined(currentCancellationPeriod(O.fromNullable(paymentResponse)))
        const refundableUntilDaysBeforeDeparture = cancellationPeriod?.validUntilDaysBeforeDeparture

        const percentage = cancellationPeriodPercentage(cancellationPeriod)
        const type = refundabilityType(paymentResponse, cancellationPeriod)

        return fareDepositRefundabilityLabel(type, refundableUntilDaysBeforeDeparture, percentage)
    },
)

const refundabilityType = (
    terms: CruisePaymentTermsResponse,
    currentCancellationPeriod: CancellationPeriod | undefined,
) => {
    if (isExchangeableForCredit(terms, currentCancellationPeriod)) return "exchangeableForCredit"

    return currentCancellationPeriod?.type === "NonRefundable" ? "nonRefundable" : "refundable"
}

const isExchangeableForCredit = (
    terms: CruisePaymentTermsResponse,
    currentCancellationPeriod: CancellationPeriod | undefined,
) => terms.depositType === "exchangeableForCredit" && currentCancellationPeriod?.type !== "NonRefundable"

export const cancellationPeriodPercentage = (currentCancellationPeriod: CancellationPeriod | undefined): number =>
    match(currentCancellationPeriod)
        .with({ type: "Proportion", penalty: { type: "percentage" } }, (props) => props.penalty.percentage)
        .with(
            { type: "Proportion", penalty: { type: "percentageWithMaximumAmount" } },
            (props) => props.penalty.percentage,
        )
        .otherwise(() => 0)

const fareDepositRefundabilityLabel = (
    depositType: CruisePaymentTermsResponse["depositType"] | undefined,
    refundableUntilDaysBeforeDeparture = 1,
    percentage: number,
): RefundabilityInfo => {
    if (!depositType) return defaultRefundabilityInfo
    const days = refundableUntilDaysBeforeDeparture - 1

    switch (depositType) {
        case "refundable": {
            return percentage === 0
                ? {
                      isRefundable: true,
                      content: t({
                          id: "deposit-refundable-content-new-asterisk",
                          message: `Deposit ${100 - percentage}%* refundable until  ${days} days pre cruise`,
                      }),
                  }
                : {
                      isRefundable: true,
                      content: t({
                          id: "deposit-refundable-content-new",
                          message: `Deposit ${100 - percentage}% refundable until  ${days} days pre cruise`,
                      }),
                  }
        }

        case "nonRefundable":
            return {
                isRefundable: false,
                content: t({
                    id: "deposit-non-refundable-content",
                    message: `Deposit not refundable`,
                }),
            }

        case "exchangeableForCredit":
            return {
                isRefundable: false,
                content: t({
                    id: "deposit-exchangeable-for-credit-content",
                    message: `Deposit convertible to FCC*`,
                }),
            }
    }
}
